import { Chip, Tooltip } from "@mui/material";
import type { AssetPlaybackBlocker } from "@shared/channel-playability";
import type { Availability } from "@shared/content-rights";
import AvailabilityChip from "./AvailabilityChip";
import { blockerText } from "./findings";
import { tokens } from "../../theme";

/**
 * What a Distribution list says about a title, when "is it licensed?" is not
 * the question the reader is asking.
 *
 * ## The misread this exists to stop
 *
 * `AvailabilityChip` reports the LICENCE WINDOW and nothing else, deliberately
 * — its docblock explains that the rights window is "the one thing with
 * storage, CRUD and an enforcement path that actually runs", and that is a
 * good reason for it to mean exactly one thing.
 *
 * But a freshly submitted title has an open window and no playable media, so
 * the column said **"Available"** about something no guest could watch and no
 * customer could be offered. Found by Sigge searching for a title he had just
 * put into the review queue. The word is true about the window and false about
 * everything the reader was actually asking.
 *
 * ## Why "missing media" outranks the window
 *
 * Because it is the earlier question. A licence window describes permission to
 * show something; with no playable rendition there is nothing to permit, and a
 * raw upload is not a rendition — the master is the file a supplier sent, not
 * anything a television can select.
 *
 * So `no_playable_media` wins, and every other state falls through to the
 * availability chip unchanged. This composes rather than extending
 * `AvailabilityChip`, so that component keeps meaning one thing and the
 * PRECEDENCE lives in one place instead of at every call site.
 *
 * ⚠️ The tooltip is `blockerText`, the same sentence the editor and the
 * overview tab draw for this code. A second wording of one finding is how two
 * screens start disagreeing about the same title.
 */
export default function TitleReadinessChip({
  availability,
  blockers,
  size = "small",
}: {
  availability: Availability;
  blockers: AssetPlaybackBlocker[];
  size?: "small" | "medium";
}) {
  const missing = blockers.find(
    (blocker) => blocker.code === "no_playable_media"
  );
  if (!missing)
    return <AvailabilityChip availability={availability} size={size} />;

  return (
    <Tooltip title={blockerText(missing)}>
      <Chip
        size={size}
        label="Missing media"
        variant="outlined"
        sx={{ color: tokens.warning, borderColor: tokens.warning }}
      />
    </Tooltip>
  );
}
